import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import { useNotify, useRefresh } from "react-admin";
import { API } from "../providers/api.provider";
import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";
import { formatCNPJ } from "../utils/mask.util";

/**
 * Formato esperado da planilha XLSX:
 * 
 * Colunas obrigatórias:
 * - nome: Nome da empresa
 * - cnpj: CNPJ (apenas números ou formatado)
 * 
 * Colunas opcionais (Endereço):
 * - cep: CEP do endereço
 * - logradouro: Rua/Avenida
 * - numero: Número do endereço
 * - complemento: Complemento
 * - bairro: Bairro
 * - cidade: Cidade
 * - estado: Estado (sigla com 2 letras)
 */

interface CompanyImportRow {
  nome: string;
  cnpj: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
}

export const ImportCompaniesButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const downloadTemplate = () => {
    // Criar planilha template
    const templateData = [
      {
        nome: "Empresa Exemplo Ltda",
        cnpj: "12345678000190",
        cep: "01310100",
        logradouro: "Avenida Paulista",
        numero: "1000",
        complemento: "Sala 101",
        bairro: "Bela Vista",
        cidade: "São Paulo",
        estado: "SP"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Empresas");
    XLSX.writeFile(wb, "template_empresas.xlsx");
  };

  const parseCompanyData = (row: CompanyImportRow) => {
    const companyData: any = {
      name: row.nome,
      cnpj: formatCNPJ(row.cnpj?.toString() || ''), // Formata CNPJ
    };

    // Endereço
    if (row.cep || row.logradouro) {
      companyData.address = {
        zipCode: row.cep?.toString().replace(/\D/g, ''),
        street: row.logradouro,
        number: row.numero,
        complement: row.complemento,
        district: row.bairro,
        city: row.cidade,
        state: row.estado,
      };
    }

    return companyData;
  };

  const handleImport = async () => {
    if (!file) {
      notify('Selecione um arquivo para importar', { type: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: CompanyImportRow[] = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        notify('A planilha está vazia', { type: 'warning' });
        setLoading(false);
        return;
      }

      // Validar campos obrigatórios
      const errors: string[] = [];
      jsonData.forEach((row, index) => {
        if (!row.nome) errors.push(`Linha ${index + 2}: Nome é obrigatório`);
        if (!row.cnpj) errors.push(`Linha ${index + 2}: CNPJ é obrigatório`);
      });

      if (errors.length > 0) {
        notify(`Erros na planilha:\n${errors.slice(0, 5).join('\n')}`, { type: 'error' });
        setLoading(false);
        return;
      }

      // Processar e enviar dados
      const companiesData = jsonData.map(parseCompanyData);

      // Enviar para o backend
      await API.post('/admin/companies/import', { companies: companiesData });

      notify(`${companiesData.length} empresa(s) importada(s) com sucesso`, { type: 'success' });
      setOpen(false);
      setFile(null);
      refresh();
    } catch (error: any) {
      console.error('Erro ao importar empresas:', error);
      notify(error?.response?.data?.message || 'Erro ao importar empresas', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={() => setOpen(true)}
      >
        <UploadIcon style={{ marginRight: 8 }} />
        Importar
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Empresas</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Formato do arquivo:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Arquivo Excel (.xlsx)<br />
                • Colunas obrigatórias: nome, cnpj<br />
                • Colunas opcionais: cep, logradouro, numero, complemento, bairro, cidade, estado
              </Typography>
              <Box mt={1}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={downloadTemplate}
                  style={{ cursor: 'pointer' }}
                >
                  Baixar planilha modelo
                </Link>
              </Box>
            </Alert>
          </Box>

          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="import-companies-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="import-companies-file">
            <Button variant="outlined" component="span" fullWidth disabled={loading}>
              {file ? file.name : 'Selecionar arquivo'}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={!file || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
